import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { fmtCAD } from "@/lib/format";
import {
  Check, MagicWand, FilePdf, Car, Pill, ArrowRight, Heart, ShieldCheck, Ticket
} from "@phosphor-icons/react";

export default function Subscribe() {
  const { user, isAuthed, loading } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState(null);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState(null);
  const [abActive, setAbActive] = useState(false);
  const [yearlyVariant, setYearlyVariant] = useState("yearly"); // "yearly" (A) | "yearly_b" (B)
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  // Persistent A/B variant assignment via localStorage (50/50 random on first visit)
  useEffect(() => {
    try {
      let v = localStorage.getItem("medeasy_yearly_variant");
      if (v !== "A" && v !== "B") {
        v = Math.random() < 0.5 ? "A" : "B";
        localStorage.setItem("medeasy_yearly_variant", v);
      }
      const variantPlan = v === "B" ? "yearly_b" : "yearly";
      setYearlyVariant(variantPlan);
      setSelectedPlan(variantPlan);
    } catch {
      // localStorage blocked → default to control
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    api.get("/billing/stats").then((r) => setStats(r.data)).catch((err) => console.warn("billing/stats failed", err));
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans);
      const hasVariantB = !!r.data.plans?.yearly_b;
      setAbActive(!!r.data.ab_yearly_test_active && hasVariantB);
      // If we assigned variant B but backend doesn't have it configured, fall back to yearly A
      if (!hasVariantB) {
        setYearlyVariant("yearly");
        setSelectedPlan((sp) => (sp === "yearly_b" ? "yearly" : sp));
      }
    }).catch((err) => console.warn("billing/plans failed", err));
    if (isAuthed) {
      api.get("/billing/me").then((r) => {
        setBilling(r.data);
        if (r.data.active) navigate("/log", { replace: true });
      }).catch((err) => console.warn("billing/me failed", err));
    }
  }, [isAuthed, loading, navigate]);

  const goLogin = () => navigate("/login?next=/subscribe");

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, plan: selectedPlan });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout");
      setBusy(false);
    }
  };

  const redeemInvite = async (overrideCode) => {
    if (!isAuthed) {
      try { sessionStorage.setItem("medeasy_pending_invite", inviteCode.trim()); } catch { /* ignore */ }
      navigate("/login?next=/subscribe");
      return;
    }
    const code = (overrideCode ?? inviteCode).trim();
    if (!code) {
      toast.error("Enter your invite code first");
      return;
    }
    setInviteBusy(true);
    try {
      // Try invite codes first; fall back to comp codes (owner-minted gifts
      // for advocacy partners) so users only ever see one "code" field.
      let data;
      try {
        ({ data } = await api.post("/invites/redeem", { code }));
        toast.success(`Welcome aboard — ${data.days_granted} days of MedEasy unlocked`);
      } catch (e1) {
        const status1 = e1?.response?.status;
        if (status1 === 404 || status1 === 400) {
          ({ data } = await api.post("/redeem-comp", { code }));
          toast.success(`Welcome aboard — ${data.days} days of MedEasy unlocked (${data.label})`);
        } else {
          throw e1;
        }
      }
      try { sessionStorage.removeItem("medeasy_pending_invite"); } catch { /* ignore */ }
      try { const r = await api.get("/billing/me"); setBilling(r.data); } catch { /* ignore */ }
      setTimeout(() => navigate("/log", { replace: true }), 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not redeem that code";
      toast.error(msg);
    } finally {
      setInviteBusy(false);
    }
  };

  // Auto-redeem if we landed here after login with a pending code
  useEffect(() => {
    if (!isAuthed) return;
    let pending = "";
    try { pending = sessionStorage.getItem("medeasy_pending_invite") || ""; } catch { /* ignore */ }
    if (pending) {
      setInviteCode(pending);
      setTimeout(() => { redeemInvite(pending); }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const monthly = plans?.monthly;
  const yearly = plans ? plans[yearlyVariant] || plans.yearly : null;
  const selected = selectedPlan === "monthly" ? monthly : (plans?.[selectedPlan] || yearly);
  const isYearlySelected = selectedPlan !== "monthly";
  const yearlySavings = (monthly && yearly) ? Math.max(0, Math.round((1 - (yearly.amount / (monthly.amount * 12))) * 100)) : 0;

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* HERO — mountain scene + control-page headline */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/37840040/pexels-photo-37840040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E2420]/85 via-[#1E2420]/55 to-[#2C4C3B]/40" />

        <div className="relative z-10">
          {/* Top bar — logo + Sign in + Hope Air pill */}
          <header className="px-6 sm:px-10 py-5 flex items-center justify-between gap-3">
            <button onClick={() => navigate("/")} data-testid="back-home" className="flex items-center gap-2">
              <div className="bg-[#F7F6F2] rounded-2xl px-3 py-2 shadow-lg">
                <img src="/medeasy-logo.png" alt="MedEasy — Medical Expenses & KM Tracker · Canada" className="h-12 sm:h-14 w-auto object-contain" />
              </div>
            </button>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="hope-air-link"
                onClick={() => navigate("/hope-air")}
                className="hidden sm:inline-flex text-sm text-white hover:underline items-center gap-1.5 bg-white/10 backdrop-blur border border-white/15 rounded-full px-3 py-1.5"
              >
                <Heart weight="fill" size={14} className="text-[#C17767]" /> 10% supports Hope Air
              </button>
              <Button
                data-testid="subscribe-header-signin"
                onClick={goLogin}
                className="rounded-full bg-white text-[#1E2420] hover:bg-white/90 font-semibold"
              >
                Sign in
              </Button>
            </div>
          </header>

          {/* Headline */}
          <div className="px-6 sm:px-10 lg:px-20 py-16 sm:py-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white/90 text-xs uppercase tracking-[0.2em] mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                Built for Canadians • CRA-friendly
              </div>
              <h1 className="font-[Manrope] font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Track every medical kilometre,
                <br />
                <span className="text-[#D4A373]">never miss a deduction.</span>
              </h1>
              <p className="text-white/80 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
                MedEasy turns your medical-travel headache into a tidy CRA-ready logbook. Snap a receipt and AI fills in the date, vendor, DIN, insurance, and out-of-pocket for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN — features (left) + pricing card (right) on cream */}
      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10">
        {/* Left — feature blocks */}
        <div>
          <ul className="space-y-3">
            {[
              { I: MagicWand, t: "AI receipt scanning", d: "Photo a receipt → form auto-fills. Prescriptions, physio, gas, parking, all categorized." },
              { I: Car, t: "Round-trip kilometres", d: "Enter one-way distance, we double it for the return drive automatically." },
              { I: ShieldCheck, t: "CRA eligibility checks", d: "MedEasy automatically applies CRA medical travel rules so you know what qualifies before tax time." },
              { I: Pill, t: "Prescriptions & services", d: "Track total, insurance covered, and out-of-pocket — the number CRA wants." },
              { I: FilePdf, t: "One-tap PDF report", d: "Tax-time ready summary for your accountant or CRA submission. Works on any phone or computer." },
            ].map(({ I, t, d }) => (
              <li key={t} className="flex gap-3">
                <div className="shrink-0 w-9 h-9 rounded-xl bg-[#2C4C3B] text-white flex items-center justify-center">
                  <I weight="duotone" size={18} />
                </div>
                <div>
                  <div className="font-medium">{t}</div>
                  <div className="text-sm text-[#5C6660]">{d}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-[#E3E1D9] bg-white p-5">
            <div className="flex items-start gap-3">
              <img src="/hopeair-logo.png" alt="Hope Air" className="h-9 w-auto object-contain shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">10% of every payment goes to Hope Air</div>
                <p className="text-sm text-[#5C6660] mt-1">
                  Hope Air is a Canadian charity that arranges <strong>free flights</strong> for people who can&apos;t afford to travel for medical care. Every MedEasy subscription helps a Canadian reach the appointment they couldn&apos;t otherwise afford to get to.
                  {" "}<button onClick={() => navigate("/hope-air")} className="text-[#2C4C3B] hover:underline font-medium" data-testid="learn-more-hope-air">Read more →</button>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — foundation code first, then pricing card */}
        <div>
          {/* Foundation / partner code — moved ABOVE the pricing card so
              new users see the FREE path before any Subscribe button.
              Beta testers and partner-foundation members (Crohn's &
              Colitis Canada, caregiver groups, etc.) redeem a code here
              to unlock a full year without payment. Users who don't
              have a code just scroll past to the pricing card below. */}
          <div
            className="rounded-2xl border-2 border-[#C17767]/40 bg-gradient-to-br from-[#FFF4ED] to-white p-5 mb-4 shadow-lg shadow-[#C17767]/10"
            data-testid="invite-code-block"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#C17767] flex items-center justify-center shrink-0">
                <Ticket weight="fill" size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Manrope] font-bold text-lg text-[#1E2420] leading-tight">
                  Got a foundation code?
                </div>
                <div className="text-xs text-[#5C6660] mt-1 leading-relaxed">
                  Unlock a full year of MedEasy <strong>free</strong> — no credit card, no Stripe.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="invite-code-input"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); redeemInvite(); } }}
                placeholder="CROHNSCOLITIS2026"
                autoComplete="off"
                spellCheck={false}
                className="flex-1 h-11 px-3 rounded-lg border border-[#E3E1D9] bg-white text-sm font-mono tracking-wider uppercase placeholder:text-[#B7B3A5] placeholder:font-sans placeholder:tracking-normal placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-[#C17767]/40"
              />
              <Button
                data-testid="invite-code-redeem"
                onClick={() => redeemInvite()}
                disabled={inviteBusy}
                className="h-11 rounded-lg bg-[#C17767] hover:bg-[#AD6757] text-white px-5 font-semibold"
              >
                {inviteBusy ? "Redeeming…" : "Redeem"}
              </Button>
            </div>
            <p className="text-[11px] text-[#5C6660] mt-2 leading-relaxed">
              Foundation, caregiver-group, and beta-tester codes shared by MedEasy.
            </p>
          </div>

          {/* Visual OR divider — makes it explicit that these are two
              alternate paths, not a code you enter DURING checkout. */}
          <div className="flex items-center gap-3 my-4 text-[10px] uppercase tracking-[0.3em] text-[#5C6660] font-semibold">
            <div className="flex-1 h-px bg-[#E3E1D9]" />
            <span>or subscribe</span>
            <div className="flex-1 h-px bg-[#E3E1D9]" />
          </div>

          <Card className="rounded-3xl border-[#E3E1D9] shadow-xl shadow-[#2C4C3B]/5 sticky top-8">
            <CardContent className="p-7">
              <div className="text-xs uppercase tracking-[0.2em] text-[#5C6660] font-semibold">MedEasy subscription</div>

              <div className="flex items-baseline gap-2 mt-5" data-testid="selected-plan-price">
                <span className="font-[Manrope] font-bold text-5xl text-[#1E2420]">
                  ${selected ? selected.amount.toFixed(2) : "49.50"}
                </span>
                <span className="text-[#5C6660]">CAD / year</span>
              </div>
              <div className="text-xs text-[#5C6660] mt-1">
                Billed once a year · cancel anytime in Settings.
              </div>

              <ul className="mt-6 space-y-2 text-sm">
                {[
                  "Unlimited trips, expenses, prescriptions",
                  "AI receipt scanning (Rx + travel receipts)",
                  "Round-trip mileage auto-calculation",
                  "PDF & CSV exports for tax filing",
                  "Receipt photo storage in cloud",
                  "Works on iPhone, Android, iPad, Mac, PC",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <Check weight="bold" size={16} className="text-[#2C4C3B] mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                {!isAuthed ? (
                  <Button
                    data-testid="subscribe-signin-cta"
                    onClick={goLogin}
                    className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
                  >
                    Sign in or create account <ArrowRight size={18} className="ml-2" />
                  </Button>
                ) : (
                  <Button
                    data-testid="subscribe-checkout"
                    onClick={checkout}
                    disabled={busy || !selected}
                    className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
                  >
                    {busy ? "Starting checkout…" : (
                      <>Subscribe — ${selected ? selected.amount.toFixed(2) : "49.50"} CAD/yr<ArrowRight size={18} className="ml-2" /></>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#5C6660] mt-3 text-center">
                Secure payment by Stripe. Auto-renews yearly. One-click cancel in Settings.
              </p>

              {stats && stats.total_paid_cad > 0 && false && (
                <div className="mt-6 pt-5 border-t border-[#E3E1D9] text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C6660]">MedEasy community has donated</div>
                  <div className="font-[Manrope] font-bold text-2xl text-[#C17767] mt-1" data-testid="hope-air-donated">
                    {fmtCAD(stats.hope_air_donated_cad)} to Hope Air
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="border-t border-[#E3E1D9] mt-12 px-6 sm:px-10 py-8 text-center text-xs text-[#5C6660] space-y-2">
        <div>MedEasy · Built in Canada · 10% of every payment supports Hope Air</div>
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigate("/terms")} className="hover:text-[#1E2420] underline" data-testid="footer-terms">Terms of Service</button>
          <span className="text-[#E3E1D9]">·</span>
          <button onClick={() => navigate("/privacy")} className="hover:text-[#1E2420] underline" data-testid="footer-privacy">Privacy Policy</button>
        </div>
      </footer>
    </div>
  );
}
